<template>
  <div>
    <!-- text button -->
    <sf-button
      v-if="!isIcon"
      id="cart-button"
      class="cart-btn"
      :loading="(loading.add && loading.supplierRowId === supplierRowId) || scheduledHandleClick"
      @click="handleClick"
      >{{ isRequest ? $t('requestPrice') : $t('addToCart') }}
    </sf-button>

    <!-- compact icon button -->
    <sf-icon-button
      v-if="isIcon"
      :loading="(loading.add && loading.supplierRowId === supplierRowId) || scheduledHandleClick"
      :aria-label="isRequest ? $t('requestPrice') : $t('addToCart')"
      @click.prevent="handleClick"
    >
      {{ buttonIcon }}
    </sf-icon-button>

    <email-request-dialog
      v-model="showEmailDialog"
      :product-id="productId"
      :supplier-row-id="supplierRowId"
    />

    <quote-request-dialog
      v-model="showQuoteDialog"
      :product-id="productId"
      :add-to-quote-event="onClickEvent.quoteRequest"
      :create-quote-event="onClickEvent.quoteRequest"
    />
  </div>
</template>

<script setup lang="ts">
import SfButton from '@/components/button/SfButton.vue'
import SfIconButton from '@/components/button/SfIconButton.vue'
import EmailRequestDialog from '@/components/dialog/EmailRequestDialog.vue'
import useCart from '@/modules/cart/useCart'
import usePortalSettings from '@/modules/portalSettings/usePortalSettings.ts'
import QuoteRequestDialog from '@/modules/quote/components/QuoteRequestDialog.vue'
import AddToCartButtonEvents from '@/modules/tracking/events/helper/addToCartButton'
import { Event } from '@/modules/tracking/types'
import { trackEvent } from '@/modules/tracking/useTracking'
import { CartItemPostDto } from '@/types/index'
import { mdiCart, mdiHandshake, mdiSend } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { computed, PropType, ref, watch } from 'vue'

const props = defineProps({
  onClickEvent: {
    type: Object as PropType<AddToCartButtonEvents>,
    required: true
  },
  isIcon: {
    type: Boolean,
    default: false
  },
  isRequest: {
    type: Boolean,
    default: false
  },
  productId: {
    type: Number,
    required: true
  },
  quantity: {
    type: Number,
    required: true
  },
  supplierRowId: {
    type: Number,
    default: undefined
  },
  updatingQuantity: {
    type: Boolean,
    default: false
  }
})

const portalSettings = usePortalSettings().portalSettings
const showEmailDialog = ref(false)
const showQuoteDialog = ref(false)

const { loading } = storeToRefs(useCart())
const scheduledHandleClick = ref(false)

const buttonIcon = computed(() => {
  if (!props.isRequest) {
    return mdiCart
  }
  if (portalSettings.useQuotes) {
    return mdiHandshake // quote request
  }
  return mdiSend // email request
})

watch(
  () => props.updatingQuantity,
  (newValue) => {
    if (!newValue && scheduledHandleClick.value) {
      scheduledHandleClick.value = false
      handleClick()
    }
  }
)

const handleClick = () => {
  if (props.updatingQuantity) {
    scheduledHandleClick.value = true
    return
  }
  let eventType: Event
  if (props.isRequest) {
    if (portalSettings.useQuotes) {
      showQuoteDialog.value = true
      eventType = props.onClickEvent.quoteRequest
    } else {
      showEmailDialog.value = true
      eventType = props.onClickEvent.emailRequest
    }
  } else {
    addToCart()
    eventType = props.onClickEvent.addToCart
  }
  trackEvent(eventType.withProduct(props.productId, props.supplierRowId))
}

const addToCart = () => {
  const item: CartItemPostDto = {
    productId: props.productId,
    quantity: props.quantity
  }
  if (props.supplierRowId) {
    item.supplierRowId = props.supplierRowId
  }
  useCart().addCartItem(item)
}
</script>
